import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const nearly = (x, y, eps = 0.01) => Math.abs(x - y) <= eps;

function round2(x) {
  return Math.round(x * 100) / 100;
}

// Wynik dokładny po usunięciu niewymierności:
// L^2 = H^2 + a^2/3
// L = sqrt((3H^2 + a^2)/3) = sqrt(3(3H^2 + a^2)) / 3 = sqrt(9H^2 + 3a^2)/3
function exactLatex(a, H) {
  const num = 9 * H * H + 3 * a * a; // 3*(3H^2 + a^2)
  return `\\dfrac{\\sqrt{${num}}}{3}`;
}

export function generateTask() {
  const a = pick([6, 8, 10, 12]);
  const H = pick([4, 5, 6]);

  // R = a/sqrt(3)
  const R = a / Math.sqrt(3);
  const L = Math.sqrt(H * H + R * R);

  return {
    type: "LateralEdgeFromHeight_TriangleRegularPyramidTask",
    data: { a, H, L },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ marginBottom: 12 }}>
      Ostrosłup <b>prawidłowy trójkątny</b> ma krawędź podstawy{" "}
      <InlineMath math={`{a=${data.a}}`} /> oraz wysokość{" "}
      <InlineMath math={`{H=${data.H}}`} />. Oblicz długość krawędzi bocznej{" "}
      <InlineMath math={`{L}`} />.
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
      <div style={{ marginBottom: 6 }}>
        <InlineMath math={`{L =}`} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <RootFractionInput
          value={userAnswer}
          onChange={onChange}
          disabled={disabled}
          label="L" // ważne: podgląd "L = ..."
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;

  const parsed =
    typeof userAnswer === "object" && userAnswer?.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  if (typeof parsed !== "number" || !Number.isFinite(parsed)) return false;

  return nearly(parsed, data.L, 0.01);
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;

  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  // spójny zapis z preview RootFractionInput
  return <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, "L")}`} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`{L=${exactLatex(data.a, data.H)}}`} />;
}

export function renderExplanation({ data }) {
  const { a, H, L } = data;
  const approx = round2(L);

  // Do rozpisania kroku 4:
  const H2 = H * H;
  const a2 = a * a;

  //  L^2 = H^2 + a^2/3 = (3H^2 + a^2)/3
  const num1 = 3 * H2 + a2;

  // po usunięciu niewymierności: L = sqrt(3*num1)/3 = sqrt(9H^2 + 3a^2)/3
  const numFinal = 3 * num1;

  return (
    <div style={{ display: "grid", rowGap: 10 }}>
      <p>
        <strong>Krok 1.</strong> Podstawa jest trójkątem równobocznym. Liczymy
        wysokość trójkąta:
      </p>
      <BlockMath math={`{h=\\tfrac{\\sqrt{3}}{2}a=\\tfrac{\\sqrt{3}}{2}\\cdot ${a}}`} />

      <p>
        <strong>Krok 2.</strong> Środek podstawy (środek okręgu opisanego) leży na
        wysokości, a promień okręgu opisanego to{" "}
        <InlineMath math={`{\\tfrac{2}{3}}`} /> tej wysokości:
      </p>
      <BlockMath math={`{R=\\tfrac{2}{3}h=\\tfrac{2}{3}\\cdot \\tfrac{\\sqrt{3}}{2}a=\\tfrac{a}{\\sqrt{3}}}`} />

      <p>
        <strong>Krok 3.</strong> Krawędź boczna <InlineMath math={`{L}`} /> jest
        przeciwprostokątną trójkąta prostokątnego o przyprostokątnych{" "}
        <InlineMath math={`{H}`} /> i <InlineMath math={`{R}`} />:
      </p>
      <BlockMath math={`{L^2=H^2+R^2}`} />

      <p>
        <strong>Krok 4.</strong> Podstawiamy dane i rozpisujemy obliczenia do wyniku:
      </p>

      <BlockMath math={`{L=\\sqrt{${H}^2+\\left(\\tfrac{${a}}{\\sqrt{3}}\\right)^2}}`} />
      <BlockMath math={`{L=\\sqrt{${H2}+\\tfrac{${a2}}{3}}}`} />
      <BlockMath math={`{L=\\sqrt{\\tfrac{3\\cdot ${H2}+${a2}}{3}}}`} />
      <BlockMath math={`{L=\\sqrt{\\tfrac{${num1}}{3}}}`} />

      <p style={{ marginTop: 2 }}>
        Usuwamy niewymierność z mianownika:
      </p>
      <BlockMath math={`{L=\\sqrt{\\tfrac{${num1}}{3}}=\\tfrac{\\sqrt{3\\cdot ${num1}}}{3}=\\tfrac{\\sqrt{${numFinal}}}{3}}`} />

      <p>
        <strong>Wynik dokładny:</strong> <InlineMath math={`{L=${exactLatex(a, H)}}`} />
      </p>

      <p style={{ opacity: 0.85 }}>
        <strong>Wartość przybliżona:</strong> <InlineMath math={`{${approx}}`} />
      </p>
    </div>
  );
}
